import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  CalendarIcon,
  Trash2,
  CheckCircle2,
  Clock,
  ClipboardList,
  Eye,
  Loader2,
  Plus,
  ArrowRight,
  AlertCircle,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import {
  useGetWPPlanDetailQuery,
  useCreateWPPlanMutation,
  useUpdateWPPlanMutation,
  useGetWPProjectsQuery,
  useGetWPTasksQuery,
  useGetWPTaskStatusesQuery,
  WPPlanRow,
  WPProject,
  WPTaskPayload,
} from '@/redux/service/weeklyPlanning'

export type DialogMode = 'plan' | 'edit' | 'submit' | 'view'

interface PlanDialogProps {
  isOpen: boolean
  onClose: () => void
  mode: DialogMode
  member: WPPlanRow | null
  weekStart: string
  onAckRequired?: () => void
}

type LocalTask = {
  clickup_task_id: string | null
  task_name: string
  project_id: number
  project_name: string
  is_billable: boolean
  estimated_hours: number
}

type PendingTask = {
  clickup_task_id: string
  task_name: string
  status: string
  time_estimate: number | null
  checked: boolean
  hours: string
  is_billable: boolean
}

function projectStatusClass(status: string | null): string {
  const s = (status ?? '').toLowerCase()
  if (s === 'active')
    return 'bg-green-500/10 text-green-600 border-green-500/20'
  if (s === 'needs attention')
    return 'bg-amber-500/10 text-amber-600 border-amber-500/20'
  if (s === 'on hold' || s === 'finished')
    return 'bg-muted text-muted-foreground border-border'
  if (!s) return 'bg-muted text-muted-foreground border-border'
  return 'bg-accent text-accent-foreground border-border'
}

function TaskSelectorPanel({
  addedTasks,
  onAddTasks,
  memberUserId,
}: {
  addedTasks: LocalTask[]
  onAddTasks: (tasks: LocalTask[]) => void
  memberUserId: number | undefined
}) {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  )
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([])
  const [includeInactive, setIncludeInactive] = useState(false)
  const hoursRefs = useRef<Map<string, HTMLInputElement | null>>(new Map())

  const { data: regularProjectsData, isLoading: loadingProjects } =
    useGetWPProjectsQuery({
      ...(memberUserId != null && { member_user_id: memberUserId }),
      ...(includeInactive && { include_inactive: true }),
    })

  const { data: statusesData, isLoading: loadingStatuses } =
    useGetWPTaskStatusesQuery(selectedProjectId!, { skip: !selectedProjectId })

  useEffect(() => {
    setSelectedStatuses([])
  }, [selectedProjectId])

  const { assignedProjects, teamProjects, backlogProjects, allProjects } =
    useMemo(() => {
      const assigned: WPProject[] = []
      const team: WPProject[] = []
      regularProjectsData?.data?.forEach(p => {
        if (p.is_assigned) assigned.push(p)
        else team.push(p)
      })
      // Backlog projects come from the same API response under backlog_projects
      const backlog: WPProject[] = (
        regularProjectsData?.backlog_projects ?? []
      ).map(p => ({
        id: p.id,
        name: p.name,
        list_id: p.list_id,
        project_type: p.project_type,
        status: p.status,
        team_id: null,
        team: p.team,
        is_assigned: 0 as const,
      }))
      return {
        assignedProjects: assigned,
        teamProjects: team,
        backlogProjects: backlog,
        allProjects: [...(regularProjectsData?.data ?? []), ...backlog],
      }
    }, [regularProjectsData])

  const { data: tasksData, isLoading: loadingTasks } = useGetWPTasksQuery(
    { project_id: selectedProjectId!, statuses: selectedStatuses },
    { skip: !selectedProjectId }
  )

  const toggleStatus = (s: string) =>
    setSelectedStatuses(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    )

  useEffect(() => {
    if (!tasksData?.data) return
    const selectedProject = allProjects.find(p => p.id === selectedProjectId)
    const defaultBillable = selectedProject
      ? selectedProject.project_type !== 'Internal' &&
        selectedProject.project_type !== 'internal'
      : true
    const alreadyAdded = new Set(
      addedTasks
        .filter(t => t.project_id === selectedProjectId)
        .map(t => t.clickup_task_id)
    )
    setPendingTasks(
      tasksData.data
        .filter(t => !alreadyAdded.has(t.clickup_task_id))
        .map(t => ({
          clickup_task_id: t.clickup_task_id,
          task_name: t.task_name,
          status: t.status,
          time_estimate: t.time_estimate,
          checked: false,
          hours: t.time_estimate
            ? String(Math.round(t.time_estimate / 3600000))
            : '',
          is_billable: defaultBillable,
        }))
    )
  }, [tasksData, addedTasks, selectedProjectId, allProjects])

  const checkedCount = pendingTasks.filter(t => t.checked).length
  const checkedHours = pendingTasks
    .filter(t => t.checked)
    .reduce((s, t) => s + (Number(t.hours) || 0), 0)
  const allChecked =
    pendingTasks.length > 0 && pendingTasks.every(t => t.checked)

  const toggleAll = (val: boolean) =>
    setPendingTasks(prev => prev.map(t => ({ ...t, checked: val })))

  const toggleTask = (id: string) => {
    let willBeChecked = false
    setPendingTasks(prev =>
      prev.map(t => {
        if (t.clickup_task_id !== id) return t
        willBeChecked = !t.checked
        return { ...t, checked: willBeChecked }
      })
    )
    if (willBeChecked) {
      requestAnimationFrame(() => {
        const el = hoursRefs.current.get(id)
        if (el) {
          el.focus()
          el.select()
        }
      })
    }
  }

  const setHours = (id: string, val: string) =>
    setPendingTasks(prev =>
      prev.map(t => (t.clickup_task_id === id ? { ...t, hours: val } : t))
    )

  const handleAddSelected = () => {
    const selected = pendingTasks.filter(t => t.checked)
    if (!selected.length) return

    const invalid = selected.filter(t => !t.hours || Number(t.hours) <= 0)
    if (invalid.length > 0) {
      toast.error('Enter estimated hours for all selected tasks')
      return
    }

    const selectedProject = allProjects.find(p => p.id === selectedProjectId)

    onAddTasks(
      selected.map(t => ({
        clickup_task_id: t.clickup_task_id,
        task_name: t.task_name,
        project_id: selectedProjectId!,
        project_name: selectedProject?.name ?? '',
        is_billable: t.is_billable,
        estimated_hours: Number(t.hours),
      }))
    )
    setPendingTasks(prev => prev.filter(t => !t.checked))
  }

  return (
    <div className='flex flex-col h-full'>
      <p className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3'>
        Select Tasks
      </p>
      {/* Project */}
      <div className='space-y-1 mb-2'>
        <div className='flex items-center justify-between'>
          <Label className='text-xs text-muted-foreground'>Project</Label>
          <label className='flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
              className='w-3 h-3 accent-primary'
            />
            Include inactive
          </label>
        </div>
        {loadingProjects ? (
          <Skeleton className='h-9 w-full' />
        ) : (
          <Select
            value={selectedProjectId?.toString() ?? ''}
            onValueChange={v => setSelectedProjectId(Number(v))}
          >
            <SelectTrigger className='h-9 text-sm border-border bg-background truncate'>
              <SelectValue placeholder='Choose a project...' />
            </SelectTrigger>
            <SelectContent>
              {assignedProjects.length === 0 &&
                teamProjects.length === 0 &&
                backlogProjects.length === 0 && (
                  <div className='px-2 py-4 text-xs text-center text-muted-foreground'>
                    No projects found
                  </div>
                )}
              {assignedProjects.length > 0 && (
                <SelectGroup>
                  <SelectLabel className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground'>
                    Assigned to member
                  </SelectLabel>
                  {assignedProjects.map(p => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      <span className='flex items-center gap-2'>
                        {p.name}
                        <span className='text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded'>
                          {p.project_type}
                        </span>
                        {p.status && (
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded border capitalize',
                              projectStatusClass(p.status)
                            )}
                          >
                            {p.status}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {assignedProjects.length > 0 && teamProjects.length > 0 && (
                <SelectSeparator />
              )}
              {teamProjects.length > 0 && (
                <SelectGroup>
                  <SelectLabel className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground'>
                    {memberUserId != null ? 'Team projects' : 'All projects'}
                  </SelectLabel>
                  {teamProjects.map(p => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      <span className='flex items-center gap-2'>
                        {p.name}
                        <span className='text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded'>
                          {p.project_type}
                        </span>
                        {p.status && (
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded border capitalize',
                              projectStatusClass(p.status)
                            )}
                          >
                            {p.status}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {backlogProjects.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground'>
                      Backlog
                    </SelectLabel>
                    {backlogProjects.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        <span className='flex items-center gap-2'>
                          {p.name}
                          <span className='text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded'>
                            {p.project_type}
                          </span>
                          {p.status && (
                            <span
                              className={cn(
                                'text-[10px] px-1.5 py-0.5 rounded border capitalize',
                                projectStatusClass(p.status)
                              )}
                            >
                              {p.status}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Status filter */}
      <div className='space-y-1 mb-3'>
        <div className='flex items-center justify-between'>
          <Label className='text-xs text-muted-foreground'>
            Status{' '}
            <span className='text-muted-foreground/70 font-normal'>
              {selectedStatuses.length > 0
                ? `(${selectedStatuses.length} selected)`
                : '(all)'}
            </span>
          </Label>
          {selectedStatuses.length > 0 && (
            <button
              type='button'
              onClick={() => setSelectedStatuses([])}
              className='text-[11px] text-muted-foreground hover:text-foreground transition-colors'
            >
              Clear
            </button>
          )}
        </div>
        {!selectedProjectId ? (
          <div className='h-9 flex items-center px-2 text-[11px] text-muted-foreground/50'>
            Pick a project to see statuses
          </div>
        ) : loadingStatuses ? (
          <div className='flex flex-wrap gap-1.5'>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className='h-6 w-16 rounded-full' />
            ))}
          </div>
        ) : statusesData?.data && statusesData.data.length > 0 ? (
          <div className='flex flex-wrap gap-1.5'>
            {statusesData.data.map(s => {
              const active = selectedStatuses.includes(s)
              return (
                <button
                  key={s}
                  type='button'
                  onClick={() => toggleStatus(s)}
                  className={cn(
                    'h-6 px-2.5 rounded-full text-[11px] font-medium border transition-colors capitalize',
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/30'
                  )}
                >
                  {s}
                </button>
              )
            })}
          </div>
        ) : (
          <div className='h-9 flex items-center px-2 text-[11px] text-muted-foreground/50'>
            No statuses available
          </div>
        )}
      </div>

      {/* Task list */}
      <div className='flex-1 flex flex-col' style={{ minHeight: '220px' }}>
        {!selectedProjectId ? (
          <div className='flex-1 flex flex-col items-center justify-center text-muted-foreground/50 gap-2 border-2 border-dashed border-border rounded-xl'>
            <ClipboardList className='h-8 w-8' />
            <p className='text-xs text-center'>Select a project to see tasks</p>
          </div>
        ) : loadingTasks ? (
          <div className='space-y-2'>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className='h-11 w-full rounded-lg' />
            ))}
          </div>
        ) : pendingTasks.length === 0 ? (
          <div className='flex-1 flex flex-col items-center justify-center text-muted-foreground/50 gap-2 border-2 border-dashed border-border rounded-xl'>
            <CheckCircle2 className='h-7 w-7' />
            <p className='text-xs'>All tasks already added</p>
          </div>
        ) : (
          <>
            {/* Select all bar */}
            <div className='flex items-center justify-between px-3 py-2 bg-muted rounded-lg mb-1.5'>
              <label className='flex items-center gap-2 cursor-pointer text-xs font-semibold text-foreground'>
                <input
                  type='checkbox'
                  checked={allChecked}
                  onChange={e => toggleAll(e.target.checked)}
                  className='w-3.5 h-3.5 accent-primary'
                />
                Select all
              </label>
              {checkedCount > 0 && (
                <span className='text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full'>
                  {checkedCount} selected · {checkedHours}h
                </span>
              )}
            </div>

            {/* Scrollable task rows */}
            <div className='flex-1 overflow-y-auto space-y-1 pr-0.5'>
              {pendingTasks.map(task => (
                <div
                  key={task.clickup_task_id}
                  onClick={() => toggleTask(task.clickup_task_id)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all select-none',
                    task.checked
                      ? 'border-primary/30 bg-accent shadow-sm'
                      : 'border-transparent bg-background hover:border-border hover:bg-muted/50'
                  )}
                >
                  <input
                    type='checkbox'
                    checked={task.checked}
                    onChange={() => toggleTask(task.clickup_task_id)}
                    onClick={e => e.stopPropagation()}
                    className='w-3.5 h-3.5 accent-primary shrink-0'
                  />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm text-foreground truncate font-medium'>
                      {task.task_name}
                    </p>
                    <div className='flex items-center gap-1.5'>
                      <span className='text-[10px] text-muted-foreground capitalize'>
                        {task.status}
                      </span>
                      {task.time_estimate != null && (
                        <>
                          <span className='text-[10px] text-muted-foreground/50'>
                            ·
                          </span>
                          <span className='text-[10px] font-semibold text-muted-foreground'>
                            {Math.round(task.time_estimate / 3600000)}h
                            <span className='font-normal text-muted-foreground/70'>
                              {' '}
                              ClickUp
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className='flex items-center gap-1 shrink-0'>
                    <Input
                      ref={el => {
                        if (el) hoursRefs.current.set(task.clickup_task_id, el)
                        else hoursRefs.current.delete(task.clickup_task_id)
                      }}
                      type='number'
                      min='0'
                      step='0.5'
                      placeholder='0'
                      value={task.hours}
                      disabled={!task.checked}
                      onChange={e => {
                        e.stopPropagation()
                        setHours(task.clickup_task_id, e.target.value)
                      }}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          ;(e.target as HTMLInputElement).blur()
                        }
                      }}
                      className={cn(
                        'h-9 w-20 text-sm font-semibold text-center transition-all',
                        task.checked
                          ? 'border-primary/30 bg-background focus-visible:ring-primary/50'
                          : 'border-border bg-muted'
                      )}
                    />
                    <span
                      className={cn(
                        'text-xs font-medium',
                        task.checked
                          ? 'text-foreground'
                          : 'text-muted-foreground/50'
                      )}
                    >
                      h
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Add button */}
            <Button
              type='button'
              disabled={checkedCount === 0}
              onClick={handleAddSelected}
              className='mt-3 w-full h-9 bg-primary text-primary-foreground hover:bg-primary/90 text-xs gap-2 shrink-0'
            >
              <ArrowRight className='h-3.5 w-3.5' />
              Add {checkedCount > 0 ? checkedCount : ''} Task
              {checkedCount !== 1 ? 's' : ''} to Plan
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

function AddedTasksPanel({
  tasks,
  onRemove,
  onToggleBillable,
  isReadOnly,
}: {
  tasks: LocalTask[]
  onRemove: (idx: number) => void
  onToggleBillable: (idx: number) => void
  isReadOnly: boolean
}) {
  const grouped = useMemo(() => {
    const map = new Map<
      number,
      { project_name: string; tasks: { task: LocalTask; idx: number }[] }
    >()
    tasks.forEach((task, idx) => {
      if (!map.has(task.project_id)) {
        map.set(task.project_id, { project_name: task.project_name, tasks: [] })
      }
      map.get(task.project_id)!.tasks.push({ task, idx })
    })
    return map
  }, [tasks])

  return (
    <div className='flex flex-col h-full'>
      <div className='flex items-center justify-between mb-3'>
        <p className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest mt-2'>
          Plan Tasks
        </p>
        {tasks.length > 0 && (
          <span className='text-xs font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full'>
            {tasks.length} · {tasks.reduce((s, t) => s + t.estimated_hours, 0)}h
          </span>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className='flex-1 flex flex-col items-center justify-center text-muted-foreground/50 gap-2 border-2 border-dashed border-border rounded-xl'>
          <Plus className='h-7 w-7' />
          <p className='text-xs text-center px-4'>
            {isReadOnly
              ? 'No plan tasks'
              : 'Select tasks on the left to add to plan'}
          </p>
        </div>
      ) : (
        <div className='flex-1 overflow-y-auto space-y-4 min-h-0'>
          {Array.from(grouped.entries()).map(([projectId, group]) => {
            const groupTotal = group.tasks.reduce(
              (s, { task }) => s + task.estimated_hours,
              0
            )
            return (
              <div key={projectId}>
                <div className='flex items-center justify-between mb-1.5'>
                  <p className='text-xs font-bold text-foreground truncate'>
                    {group.project_name}
                  </p>
                  <span className='text-[11px] text-muted-foreground shrink-0 ml-2'>
                    {groupTotal}h total
                  </span>
                </div>
                <div className='space-y-1'>
                  {group.tasks.map(({ task, idx }) => (
                    <div
                      key={idx}
                      className='flex items-center gap-3 px-3 py-2.5 bg-card rounded-lg border border-border group/row hover:border-foreground/20 transition-colors'
                    >
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-foreground truncate'>
                          {task.task_name}
                        </p>
                      </div>
                      {isReadOnly ? (
                        <span
                          className={cn(
                            'text-[11px] font-semibold px-2 py-1 rounded-md border shrink-0',
                            task.is_billable
                              ? 'bg-green-500/10 text-green-600 border-green-500/20'
                              : 'bg-muted text-muted-foreground border-border'
                          )}
                        >
                          {task.is_billable ? 'Billable' : 'Non-Billable'}
                        </span>
                      ) : (
                        <button
                          type='button'
                          onClick={() => onToggleBillable(idx)}
                          title={
                            task.is_billable
                              ? 'Click to mark Non-Billable'
                              : 'Click to mark Billable'
                          }
                          className={cn(
                            'h-7 px-2.5 text-[11px] font-bold rounded-md border shrink-0 transition-colors',
                            task.is_billable
                              ? 'bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20'
                              : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                          )}
                        >
                          {task.is_billable ? 'Billable' : 'Non-Billable'}
                        </button>
                      )}
                      <span className='text-sm font-bold text-foreground flex items-center gap-1 shrink-0'>
                        <Clock className='h-3 w-3 text-muted-foreground' />
                        {task.estimated_hours}h
                      </span>
                      {!isReadOnly && (
                        <button
                          type='button'
                          onClick={() => onRemove(idx)}
                          className='opacity-0 group-hover/row:opacity-100 text-destructive/70 hover:text-destructive transition-all shrink-0'
                        >
                          <Trash2 className='h-3.5 w-3.5' />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const PlanDialog: React.FC<PlanDialogProps> = ({
  isOpen,
  onClose,
  mode,
  member,
  weekStart,
  onAckRequired,
}) => {
  const isReadOnly = mode === 'view'
  const isEdit = mode === 'edit' || mode === 'submit'
  const planId = member?.plan_id

  const { data: planDetail, isLoading: loadingDetail } =
    useGetWPPlanDetailQuery(planId!, { skip: !planId || mode === 'plan' })

  const [createPlan, { isLoading: isCreating }] = useCreateWPPlanMutation()
  const [updatePlan, { isLoading: isUpdating }] = useUpdateWPPlanMutation()

  const [planTasks, setPlanTasks] = useState<LocalTask[]>([])
  const [estBenchDate, setEstBenchDate] = useState<Date | undefined>()
  const [benchDateOpen, setBenchDateOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [attemptedSave, setAttemptedSave] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setAttemptedSave(false)

    if (planDetail?.data && (isEdit || isReadOnly)) {
      const d = planDetail.data
      setPlanTasks(
        d.plan_projects.flatMap(pg =>
          pg.tasks.map(t => ({
            clickup_task_id: t.clickup_task_id,
            task_name: t.task_name,
            project_id: pg.project_id,
            project_name: pg.project_name,
            is_billable: t.is_billable,
            estimated_hours: Number(t.estimated_hours),
          }))
        )
      )
      setNotes(d.notes ?? '')
      if (d.est_bench_date) setEstBenchDate(new Date(d.est_bench_date))
    } else if (mode === 'plan') {
      setPlanTasks([])
      setNotes('')
      setEstBenchDate(undefined)
    }
  }, [isOpen, planDetail, mode, isEdit, isReadOnly])

  const allTasks = useMemo<WPTaskPayload[]>(
    () =>
      planTasks.map(t => ({
        project_id: t.project_id,
        clickup_task_id: t.clickup_task_id,
        task_name: t.task_name,
        is_billable: t.is_billable,
        estimated_hours: t.estimated_hours,
        is_backlog: false,
      })),
    [planTasks]
  )

  const isAlreadySubmitted = member?.plan_status === 'submitted'

  const hasChanges = useMemo(() => {
    if (!planDetail?.data) return false
    const d = planDetail.data

    if ((notes ?? '') !== (d.notes ?? '')) return true

    const currentBench = estBenchDate
      ? format(estBenchDate, 'yyyy-MM-dd')
      : null
    const originalBench = d.est_bench_date
      ? format(new Date(d.est_bench_date), 'yyyy-MM-dd')
      : null
    if (currentBench !== originalBench) return true

    const fingerprint = (
      list: {
        clickup_task_id: string | null
        task_name: string
        project_id: number
        is_billable: boolean
        estimated_hours: number
      }[]
    ) =>
      list
        .map(
          t =>
            `${t.project_id}|${t.clickup_task_id ?? ''}|${t.task_name}|${t.is_billable ? 1 : 0}|${Number(t.estimated_hours)}`
        )
        .sort()
        .join(';')

    const originalAll = d.plan_projects.flatMap(pg =>
      pg.tasks.map(t => ({
        clickup_task_id: t.clickup_task_id,
        task_name: t.task_name,
        project_id: pg.project_id,
        is_billable: t.is_billable,
        estimated_hours: Number(t.estimated_hours),
      }))
    )

    return fingerprint(planTasks) !== fingerprint(originalAll)
  }, [planDetail, planTasks, notes, estBenchDate])

  const showSubmitButton = !isAlreadySubmitted || hasChanges
  const [actionType, setActionType] = useState<'draft' | 'submit' | null>(null)

  const handleSave = async (submit = false) => {
    if (!member) return
    setActionType(submit ? 'submit' : 'draft')
    if (!estBenchDate) {
      setAttemptedSave(true)
      setActionType(null)
      toast.error('Estimated Bench Date is required')
      return
    }

    if (allTasks.length === 0) {
      setActionType(null)
      toast.error('Please add at least one task to the plan')
      return
    }

    const benchDateStr = format(estBenchDate, 'yyyy-MM-dd')
    try {
      if (mode === 'plan') {
        const created = await createPlan({
          member_user_id: member.user_id,
          week_start: weekStart,
          est_bench_date: benchDateStr,
          notes: notes || null,
          tasks: allTasks,
        }).unwrap()
        if (submit && created.data?.plan_id) {
          await updatePlan({
            id: created.data.plan_id,
            est_bench_date: benchDateStr,
            notes: notes || null,
            submit: true,
            tasks: allTasks,
          }).unwrap()
          toast.success('Plan created and submitted')
        } else {
          toast.success('Plan created as draft')
        }
      } else {
        await updatePlan({
          id: planId!,
          est_bench_date: benchDateStr,
          notes: notes || null,
          submit,
          tasks: allTasks,
        }).unwrap()
        toast.success(submit ? 'Plan submitted successfully' : 'Plan saved')
      }
      onClose()
    } catch (err) {
      const error = err as { data?: { message?: string }; status?: number }
      if (error?.status === 412 && onAckRequired) {
        onAckRequired()
        return
      }
      if (error?.status === 409) {
        toast.error('A plan already exists for this member and week')
      } else {
        toast.error(error?.data?.message ?? 'Something went wrong')
      }
    } finally {
      setActionType(null)
    }
  }

  const isSaving = isCreating || isUpdating

  const titleMap: Record<DialogMode, string> = {
    plan: 'Create Plan',
    edit: 'Edit Plan',
    submit: 'Submit Plan',
    view: 'View Plan',
  }

  const totalBillable = planTasks
    .filter(t => t.is_billable)
    .reduce((s, t) => s + t.estimated_hours, 0)
  const totalNonBillable = planTasks
    .filter(t => !t.is_billable)
    .reduce((s, t) => s + t.estimated_hours, 0)

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent
        className='!w-[98vw] !max-w-none p-0 bg-background rounded-2xl overflow-auto flex flex-col border-border'
        style={{ height: '95vh' }}
      >
        {/* Header */}
        <DialogHeader className='px-6 py-4 border-b border-border bg-muted/30 shrink-0'>
          <DialogTitle className='flex items-center gap-3'>
            <div className='p-2 bg-primary text-primary-foreground rounded-lg shrink-0 shadow-sm'>
              {isReadOnly ? (
                <Eye className='w-4 h-4' />
              ) : (
                <ClipboardList className='w-4 h-4' />
              )}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-base font-bold text-foreground'>
                {titleMap[mode]}
              </p>
              {member && (
                <p className='text-sm font-normal text-muted-foreground mt-0.5 truncate'>
                  {member.member_name}{' '}
                  <span className='text-muted-foreground/50'>·</span>{' '}
                  {member.team}{' '}
                  <span className='text-muted-foreground/50'>·</span> Week of{' '}
                  {format(parseISO(weekStart), 'd MMM yyyy')}
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {loadingDetail && (isEdit || isReadOnly) ? (
          <div className='flex-1 flex items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <div className='flex-1 flex flex-col'>
            {/* Task area */}
            <div
              className={cn(
                'flex h-[600px]',
                isReadOnly ? 'block' : 'grid grid-cols-[1fr_1fr] gap-4'
              )}
            >
              {/* Left: selector — hidden in read-only */}
              {!isReadOnly && (
                <div className='overflow-y-auto flex flex-col p-5 bg-muted/10'>
                  <TaskSelectorPanel
                    addedTasks={planTasks}
                    onAddTasks={tasks =>
                      setPlanTasks(prev => [...prev, ...tasks])
                    }
                    memberUserId={member?.user_id}
                  />
                </div>
              )}

              {/* Right: added tasks */}
              <div className='overflow-y-auto flex flex-col p-5 h-full bg-background'>
                <AddedTasksPanel
                  tasks={planTasks}
                  onRemove={idx =>
                    setPlanTasks(prev => prev.filter((_, i) => i !== idx))
                  }
                  onToggleBillable={idx =>
                    setPlanTasks(prev =>
                      prev.map((t, i) =>
                        i === idx ? { ...t, is_billable: !t.is_billable } : t
                      )
                    )
                  }
                  isReadOnly={isReadOnly}
                />

                {/* Totals */}
                {planTasks.length > 0 && (
                  <div className='grid grid-cols-3 gap-2 pt-3 border-t border-border mt-3'>
                    <div className='bg-green-500/10 rounded-xl p-2.5 text-center'>
                      <p className='text-[10px] text-green-500 font-bold uppercase tracking-wide'>
                        Billable
                      </p>
                      <p className='text-lg font-bold text-green-600'>
                        {totalBillable}h
                      </p>
                    </div>
                    <div className='bg-muted rounded-xl p-2.5 text-center'>
                      <p className='text-[10px] text-muted-foreground font-bold uppercase tracking-wide'>
                        Non-Bill.
                      </p>
                      <p className='text-lg font-bold text-foreground'>
                        {totalNonBillable}h
                      </p>
                    </div>
                    <div className='bg-primary/10 rounded-xl p-2.5 text-center'>
                      <p className='text-[10px] text-primary/70 font-bold uppercase tracking-wide'>
                        Total
                      </p>
                      <p className='text-lg font-bold text-primary'>
                        {totalBillable + totalNonBillable}h
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom: bench date + notes */}
            <div className='shrink-0 border-t border-border bg-card px-6 py-4 flex gap-6 items-start'>
              <div className='space-y-1.5 w-52'>
                <Label className='text-xs font-bold text-muted-foreground uppercase tracking-wider'>
                  Est. Bench Date
                  {!isReadOnly && <span className='text-destructive'>*</span>}
                </Label>
                <Popover open={benchDateOpen} onOpenChange={setBenchDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='outline'
                      disabled={isReadOnly}
                      className={cn(
                        'w-full h-9 justify-start text-left font-normal text-sm bg-background border-border shadow-sm',
                        estBenchDate
                          ? 'border-border'
                          : attemptedSave && !isReadOnly
                            ? 'border-destructive/50 bg-destructive/10 text-destructive hover:bg-destructive/20'
                            : 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className='mr-2 h-4 w-4' />
                      {estBenchDate
                        ? format(estBenchDate, 'dd MMM yyyy')
                        : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-auto p-0 bg-popover border-border rounded-xl shadow-xl'
                    align='start'
                  >
                    <Calendar
                      mode='single'
                      captionLayout='dropdown'
                      selected={estBenchDate}
                      onSelect={d => {
                        setEstBenchDate(d)
                        setBenchDateOpen(false)
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {!isReadOnly && attemptedSave && !estBenchDate && (
                  <p className='text-[10px] text-destructive flex items-center gap-1 mt-0.5'>
                    <AlertCircle className='h-3 w-3' />
                    Required to save or submit
                  </p>
                )}
              </div>

              <div className='space-y-1.5 w-full'>
                <Label className='text-xs font-bold text-muted-foreground uppercase tracking-wider'>
                  Additional Notes
                </Label>
                <textarea
                  rows={2}
                  disabled={isReadOnly}
                  placeholder='Any notes for this week...'
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className='w-full text-sm rounded-lg border border-border bg-background px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-muted disabled:text-muted-foreground leading-relaxed shadow-sm'
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className='shrink-0 flex gap-3 px-6 py-4 border-t border-border bg-muted/30'>
              <Button
                variant='outline'
                onClick={onClose}
                className='h-10 px-6 font-semibold rounded-xl bg-background border-border shadow-sm'
                disabled={isSaving}
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </Button>

              {!isReadOnly && (
                <div className='ml-auto flex gap-3'>
                  {mode === 'edit' && !isAlreadySubmitted && (
                    <Button
                      onClick={() => handleSave(false)}
                      disabled={isSaving}
                      variant='secondary'
                      className='h-10 px-6 font-semibold rounded-xl shadow-sm'
                    >
                      {actionType === 'draft' && isSaving ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <>
                          <ClipboardList className='h-4 w-4' />
                          Save Draft
                        </>
                      )}
                    </Button>
                  )}
                  {(mode === 'submit' || mode === 'edit') &&
                    showSubmitButton && (
                      <Button
                        onClick={() => handleSave(true)}
                        disabled={isSaving}
                        className='h-10 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl gap-2 shadow-sm'
                      >
                        {actionType === 'submit' && isSaving ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <>
                            <CheckCircle2 className='h-4 w-4' />
                            {isAlreadySubmitted
                              ? 'Re-submit Plan'
                              : 'Submit Plan'}
                          </>
                        )}
                      </Button>
                    )}
                  {mode === 'plan' && (
                    <>
                      <Button
                        onClick={() => handleSave(false)}
                        disabled={isSaving}
                        variant='secondary'
                        className='h-10 px-6 font-semibold rounded-xl shadow-sm gap-2'
                      >
                        {actionType === 'draft' && isSaving ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <>
                            <ClipboardList className='h-4 w-4' />
                            Save as Draft
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleSave(true)}
                        disabled={isSaving}
                        className='h-10 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl gap-2 shadow-sm'
                      >
                        {actionType === 'submit' && isSaving ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <>
                            <CheckCircle2 className='h-4 w-4' />
                            Create & Submit
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PlanDialog
